import React, { useMemo } from 'react';
import { formatCurrency } from '../../utils/currencyUtils';
import { getExpenseTheme } from './expenseGroupTheme';
import ExpenseCard from './ExpenseCard';
import { SortableHeader } from '../../utils/tableSort';
import PaginationControls from '../../utils/PaginationControls';
import {
  Banknote,
  Calendar,
  CreditCard,
  Filter,
  List,
  Pencil,
  Receipt,
  Search,
  Trash2,
  Wallet,
} from 'lucide-react';

function monthKey(dateStr) {
  if (!dateStr) return 'unknown';
  return dateStr.slice(0, 7);
}

function formatMonthLabel(monthStr) {
  if (!monthStr || monthStr === 'unknown') return 'Unknown period';
  const d = new Date(`${monthStr}-01`);
  if (Number.isNaN(d.getTime())) return monthStr;
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function formatPayDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function PaymentModeBadge({ mode }) {
  const m = mode || 'Cash';
  const styles = {
    Cash: 'bg-slate-100 text-slate-800 ring-slate-200',
    'Bank Transfer': 'bg-blue-100 text-blue-800 ring-blue-200',
    UPI: 'bg-violet-100 text-violet-800 ring-violet-200',
    Cheque: 'bg-amber-100 text-amber-800 ring-amber-200',
    Card: 'bg-slate-100 text-slate-700 ring-slate-200',
    Other: 'bg-gray-100 text-gray-700 ring-gray-200',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
        styles[m] || styles.Other
      }`}
    >
      <CreditCard className="h-3 w-3 opacity-70" aria-hidden />
      {m}
    </span>
  );
}

export default function ExpenseListHistory({
  variant,
  filteredExpenses,
  companyCurrency,
  totalAmount,
  searchQuery,
  dateFilter,
  amountFilter,
  onSearchChange,
  onDateChange,
  onAmountChange,
  onClearFilters,
  tablePagination,
  sortConfig,
  tableHandleSort,
  showMobileFilters,
  setShowMobileFilters,
  isCardExpanded,
  toggleCard,
  onEdit,
  onDelete,
  showPersonColumn = false,
  onSettleFromWallet,
}) {
  const theme = getExpenseTheme(variant);
  const fmt = (n) => formatCurrency(Number(n) || 0, companyCurrency);
  const hasFilters = searchQuery || dateFilter || amountFilter;
  const expenses = tablePagination.currentData || [];
  const listCount = filteredExpenses?.length ?? expenses.length;

  const numbered = useMemo(
    () => expenses.map((e, i) => ({ expense: e, rowNum: (tablePagination.startIndex || 0) + i + 1 })),
    [expenses, tablePagination.startIndex]
  );

  const groupedByMonth = useMemo(() => {
    const groups = [];
    let currentKey = null;
    numbered.forEach(({ expense, rowNum }) => {
      const key = monthKey(expense.date);
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ monthKey: key, label: formatMonthLabel(key), entries: [] });
      }
      groups[groups.length - 1].entries.push({ expense, rowNum });
    });
    // Month sections follow table sort; when sorted by date, keep newest months first
    if (sortConfig?.key === 'date' && sortConfig?.direction === 'desc') {
      return groups.sort((a, b) => b.monthKey.localeCompare(a.monthKey));
    }
    if (sortConfig?.key === 'date' && sortConfig?.direction === 'asc') {
      return groups.sort((a, b) => a.monthKey.localeCompare(b.monthKey));
    }
    return groups;
  }, [numbered, sortConfig?.key, sortConfig?.direction]);

  const filterInputClass = `w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm focus:bg-white focus:outline-none focus:ring-1 ${theme.filterFocus}`;
  const filterSelectClass = `rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 ${theme.filterFocus}`;

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-md overflow-hidden ring-1 ring-black/5">
      <div className={`bg-gradient-to-r ${theme.header} px-5 py-5 text-white`}>
        <h3 className="text-lg font-semibold tracking-tight">{theme.historyTitle}</h3>
        <p className="text-slate-300 text-sm mt-1">
          {listCount} record{listCount !== 1 ? 's' : ''} · Total {fmt(totalAmount)}
        </p>
      </div>

      <div className="hidden lg:grid grid-cols-2 lg:grid-cols-3 gap-3 p-4 bg-gradient-to-b from-slate-50/80 to-white border-b border-gray-100">
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className={`flex items-center gap-2 ${theme.filterIcon} mb-1`}>
            <List className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Entries</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{listCount}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm col-span-1 lg:col-span-2">
          <div className={`flex items-center gap-2 ${theme.filterIcon} mb-1`}>
            <Banknote className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Total amount</span>
          </div>
          <p className={`text-lg font-bold truncate ${theme.amount}`}>{fmt(totalAmount)}</p>
        </div>
      </div>

      <div className="hidden lg:block px-4 py-3 border-b border-gray-100 bg-white space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <Filter className={`h-4 w-4 ${theme.filterIcon}`} />
          Filters
        </div>
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={theme.searchPlaceholder}
              className={filterInputClass}
            />
          </div>
          <input type="date" value={dateFilter} onChange={(e) => onDateChange(e.target.value)} className={filterSelectClass} />
          <input
            type="number"
            value={amountFilter}
            onChange={(e) => onAmountChange(e.target.value)}
            placeholder="Amount"
            className={`${filterSelectClass} lg:w-36`}
          />
          {hasFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className={`text-sm font-medium whitespace-nowrap px-2 ${theme.filterIcon} hover:opacity-80`}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="hidden lg:block">
        {listCount === 0 ? (
          <div className="py-16 text-center">
            <div className={`mx-auto w-14 h-14 rounded-full ${theme.emptyBg} flex items-center justify-center mb-3`}>
              <Receipt className={`h-7 w-7 ${theme.emptyIcon}`} />
            </div>
            <p className="text-gray-500 font-medium">No expenses found</p>
            <p className="text-sm text-gray-400 mt-1">
              {hasFilters ? 'Try adjusting filters' : 'Add your first expense above'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-800 text-white text-left text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-3 w-12 text-center">#</th>
                  <SortableHeader
                    columnKey="date"
                    label="Date"
                    onSort={tableHandleSort}
                    sortConfig={sortConfig}
                    className="px-4 py-3 cursor-pointer hover:text-slate-200"
                  />
                  <SortableHeader
                    columnKey="head"
                    label="Category"
                    onSort={tableHandleSort}
                    sortConfig={sortConfig}
                    className="px-4 py-3 cursor-pointer hover:text-slate-200"
                  />
                  <th className="px-4 py-3">Entry</th>
                  {showPersonColumn && <th className="px-4 py-3">Person</th>}
                  <SortableHeader
                    columnKey="amount"
                    label="Amount"
                    onSort={tableHandleSort}
                    sortConfig={sortConfig}
                    className="px-4 py-3 text-right cursor-pointer hover:text-slate-200"
                  />
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3">Settlement</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3 text-center w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedByMonth.map((group) => (
                  <React.Fragment key={group.monthKey}>
                    <tr className={theme.groupRow}>
                      <td colSpan={showPersonColumn ? 10 : 9} className="px-4 py-2.5">
                        <div className={`flex items-center gap-2 font-semibold text-xs uppercase tracking-wide`}>
                          <Calendar className="h-4 w-4" />
                          {group.label}
                          <span className={`font-normal normal-case ${theme.groupSub}`}>
                            ({group.entries.length} entr{group.entries.length !== 1 ? 'ies' : 'y'})
                          </span>
                        </div>
                      </td>
                    </tr>
                    {group.entries.map(({ expense, rowNum }) => {
                      const zebra = rowNum % 2 === 0;
                      return (
                        <tr
                          key={expense.id}
                          className={`border-b border-gray-100 transition-colors ${theme.hoverRow} ${
                            zebra ? 'bg-white' : 'bg-slate-50/60'
                          }`}
                        >
                          <td className="px-4 py-3 text-center text-xs font-mono text-gray-400">{rowNum}</td>
                          <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">
                            {formatPayDate(expense.date)}
                          </td>
                          <td className="px-4 py-3 text-gray-800">{expense.head || '—'}</td>
                          <td className="px-4 py-3 text-gray-600">{expense.constantEntry || '—'}</td>
                          {showPersonColumn && (
                            <td className="px-4 py-3 text-gray-600 max-w-[140px] truncate">
                              {expense.personName || '—'}
                            </td>
                          )}
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-block rounded-lg px-2.5 py-1 font-bold tabular-nums ${theme.amountBg}`}>
                              {fmt(expense.amount)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <PaymentModeBadge mode={expense.paymentMode} />
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset ${
                                expense.settled
                                  ? 'bg-emerald-100 text-emerald-800 ring-emerald-200'
                                  : 'bg-red-100 text-red-800 ring-red-200'
                              }`}
                            >
                              {expense.settled ? 'Settled' : 'Unsettled'}
                            </span>
                            {expense.settledFromOtherWallet?.sourceEmployeeName && (
                              <p className="mt-0.5 text-[10px] text-gray-500 truncate max-w-[120px]">
                                {expense.settledFromOtherWallet.sourceEmployeeName}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">
                            {expense.description || '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center gap-0.5">
                              {!expense.settled && onSettleFromWallet && (
                                <button
                                  type="button"
                                  onClick={() => onSettleFromWallet(expense)}
                                  className={`p-2 rounded-lg ${theme.filterIcon} hover:bg-slate-100`}
                                  title="Settle from employee wallet"
                                >
                                  <Wallet className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => onEdit(expense)}
                                className={`p-2 rounded-lg ${theme.filterIcon} hover:bg-slate-100`}
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => onDelete(expense.id)}
                                className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 border-t-2 border-slate-300 font-semibold text-sm">
                  <td colSpan={showPersonColumn ? 5 : 4} className="px-4 py-3 text-right text-gray-700 uppercase text-xs tracking-wide">
                    Total ({listCount})
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums ${theme.amount}`}>{fmt(totalAmount)}</td>
                  <td colSpan={showPersonColumn ? 4 : 3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {listCount > 0 && (
          <div className="px-4 py-3 border-t border-gray-100">
            <PaginationControls {...tablePagination} />
          </div>
        )}
      </div>

      <div className="lg:hidden p-4 space-y-4">
        <div className="relative mobile-filter-dropdown">
          <button
            type="button"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
              hasFilters ? theme.mobileFilterActive : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span className="text-sm font-medium">Filters</span>
            {hasFilters && (
              <span className={`${theme.mobileFilterBadge} text-white text-xs px-1.5 py-0.5 rounded-full`}>
                {[searchQuery, dateFilter, amountFilter].filter(Boolean).length}
              </span>
            )}
          </button>
          {showMobileFilters && (
            <div className="absolute top-full left-0 right-0 mt-2 z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-4 space-y-3">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={theme.searchPlaceholder}
                className={filterInputClass}
              />
              <input type="date" value={dateFilter} onChange={(e) => onDateChange(e.target.value)} className={filterSelectClass} />
              <input
                type="number"
                value={amountFilter}
                onChange={(e) => onAmountChange(e.target.value)}
                placeholder="Amount"
                className={filterSelectClass}
              />
              {hasFilters && (
                <button type="button" onClick={onClearFilters} className="w-full py-2 bg-slate-600 text-white rounded-lg text-sm">
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
        {listCount === 0 ? (
          <p className="text-center py-8 text-gray-400 text-sm">No expenses</p>
        ) : (
          <>
            <div className="space-y-3">
              {tablePagination.currentData.map((expense) => (
                <ExpenseCard
                  key={expense.id}
                  expense={expense}
                  isExpanded={isCardExpanded(expense.id)}
                  onToggle={toggleCard}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onSettleFromWallet={onSettleFromWallet}
                  companyCurrency={companyCurrency}
                  accent={theme.accent}
                />
              ))}
            </div>
            <PaginationControls {...tablePagination} />
          </>
        )}
      </div>
    </div>
  );
}
